
import StepsHeroSection from "../components/landing/StepsHeroSection"
import RegistrationOfficesSection from "../components/landing/RegistrationOfficesSection"
import InstantFilingSection from "../components/landing/InstantFilingSection"
import AnnouncementsSection from "../components/landing/AnnouncementsSection"
import LandingTextSection from "../components/landing/LandingTextSection"
import Footer from "../components/ui/Footer"
import { useSiteContent } from "../hooks/useSiteContent"

const DEFAULT_STEPS = [
  {
    title: "Application information",
    text: "Tell us who is applying and provide the basic information required for your application.",
  },
  {
    title: "Content",
    text: "Upload the documents and information required for your patent, trademark or design application.",
  },
  {
    title: "Additional information",
    text: "Provide any additional information, priority details or requests required for your application.",
  },
  {
    title: "Payment",
    text: "Review the applicable fees and pay by invoice or credit card. Official fees are paid after we have received your payment.",
  },
]

const DEFAULT_OFFICES = [
  {
    label: "EUIPO",
    subtitle: "Trademark",
    text: "European Union Intellectual Property Office (EUIPO)",
    button: {
      src: "/images/btn-read-more.svg",
      label: "Read more",
      path: "/trademark",
    },
  },
  {
    label: "EUIPO",
    subtitle: "Design",
    text: "European Union Intellectual Property Office (EUIPO)",
    button: {
      src: "/images/btn-read-more.svg",
      label: "Read more",
      path: "/design",
    },
  },
  {
    label: "EPO",
    subtitle: "Patent",
    text: "European Patent Office (EPO)",
    button: {
      src: "/images/btn-read-more.svg",
      label: "Read more",
      path: "/patent",
    },
  },
]

const DEFAULT_IP_RIGHTS = [
  {
    title: "Trademark",
    image: "/images/trademark.svg",
    text: "Protect your brand and commercial identity across the European Union.",
    path: "/trademark",
  },
  {
    title: "Design",
    image: "/images/design.svg",
    text: "Protect the appearance of your product across the European Union.",
    path: "/design",
  },
  {
    title: "Patent",
    image: "/images/patent.svg",
    text: "Protect your technical invention through the European patent system.",
    path: "/patent",
  },
]

const DEFAULT_ANNOUNCEMENTS = [
  {
    label: "EU Patent from 03/2027",
    description: "Information about the upcoming European patent filing option.",
  },
  {
    label: "EU Design from 09/2026",
    description: "Information about the European design filing option.",
  },
  {
    label: "DE Trademark",
    description: "Information about German trademark filings.",
  },
  {
    label: "DE Design",
    description: "Information about German design filings.",
  },
  {
    label: "DE Patent",
    description: "Information about German patent filings.",
  },
]

export default function InstantFilingPage() {
  const site = useSiteContent("instant-filing")

  const steps = DEFAULT_STEPS.map((step, index) => ({
    title: site.get(`instant-filing.hero.step.${index}.title`, step.title),
    text: site.get(`instant-filing.hero.step.${index}.text`, step.text),
  }))

  const offices = DEFAULT_OFFICES.map((office, index) => ({
    ...office,
    label: site.get(`instant-filing.offices.office.${index}.label`, office.label),
    subtitle: site.get(`instant-filing.offices.office.${index}.subtitle`, office.subtitle),
    text: site.get(`instant-filing.offices.office.${index}.text`, office.text),
  }))

  const ipRights = DEFAULT_IP_RIGHTS.map((card, index) => ({
    ...card,
    title: site.get(`instant-filing.rights.card.${index}.title`, card.title),
    text: site.get(`instant-filing.rights.card.${index}.text`, card.text),
  }))

  const announcements = DEFAULT_ANNOUNCEMENTS.map((item, index) => ({
    label: site.get(`instant-filing.announcements.item.${index}.label`, item.label),
    description: site.get(
      `instant-filing.announcements.item.${index}.description`,
      item.description
    ),
  }))

  return (
    <div className="flex min-h-screen flex-col">
      {site.isAdmin && (
        <button
          type="button"
          onClick={() => site.setEditMode(!site.editMode)}
          className={`fixed bottom-6 right-6 z-50 rounded-full border-2 border-white px-5 py-3 text-sm font-semibold shadow-xl transition ${
            site.editMode
              ? "bg-orange-500 text-white"
              : "bg-dantzBlue text-white"
          }`}
        >
          {site.editMode ? "Done editing" : "Edit page"}
        </button>
      )}

      <StepsHeroSection
        heading={site.get("instant-filing.hero.heading", "INSTANT FILING")}
        description={site.get(
          "instant-filing.hero.description",
          "xpress-ip gives companies and IP professionals outside Europe a simple way to file patents, trademarks and designs in Europe. Complete the required information online and upload your documents. THE XPRESS IP COMPANY AG handles the filing process in cooperation with DANTZ IP & LAW."
        )}
        steps={steps}
        editable={site.editMode}
        onSaveHeading={(value) =>
          site.save("instant-filing.hero.heading", value)
        }
        onSaveDescription={(value) =>
          site.save("instant-filing.hero.description", value)
        }
        onSaveStepTitle={(index, value) =>
          site.save(`instant-filing.hero.step.${index}.title`, value)
        }
        onSaveStepText={(index, value) =>
          site.save(`instant-filing.hero.step.${index}.text`, value)
        }
      />

      <RegistrationOfficesSection
        offices={offices}
        subtitle={site.get(
          "instant-filing.offices.subtitle",
          "File where you need protection"
        )}
        editable={site.editMode}
        onSaveSubtitle={(value) =>
          site.save("instant-filing.offices.subtitle", value)
        }
        onSaveOfficeLabel={(index, value) =>
          site.save(`instant-filing.offices.office.${index}.label`, value)
        }
        onSaveOfficeText={(index, value) =>
          site.save(`instant-filing.offices.office.${index}.text`, value)
        }
        onSaveOfficeSubtitle={(index, value) =>
          site.save(`instant-filing.offices.office.${index}.subtitle`, value)
        }
      />

      <InstantFilingSection
        id="intellectual-property-rights"
        heading={site.get(
          "instant-filing.rights.heading",
          "INTELLECTUAL PROPERTY RIGHTS"
        )}
        types={ipRights}
        editable={site.editMode}
        onSaveHeading={(value) =>
          site.save("instant-filing.rights.heading", value)
        }
        onSaveCardTitle={(index, value) =>
          site.save(`instant-filing.rights.card.${index}.title`, value)
        }
        onSaveCardText={(index, value) =>
          site.save(`instant-filing.rights.card.${index}.text`, value)
        }
      />

      <AnnouncementsSection
        subtitle={site.get(
          "instant-filing.announcements.subtitle",
          "Important European IP developments"
        )}
        items={announcements}
        editable={site.editMode}
        onSaveSubtitle={(value) =>
          site.save("instant-filing.announcements.subtitle", value)
        }
        onSaveItemLabel={(index, value) =>
          site.save(`instant-filing.announcements.item.${index}.label`, value)
        }
        onSaveItemDescription={(index, value) =>
          site.save(
            `instant-filing.announcements.item.${index}.description`,
            value
          )
        }
      />

      <LandingTextSection
        heading={site.get(
          "instant-filing.options.heading",
          "ADDITIONAL FILING OPTIONS"
        )}
        subheading={site.get(
          "instant-filing.options.subheading",
          "File through experienced European IP professionals"
        )}
        editable={site.editMode}
        onSaveHeading={(value) =>
          site.save("instant-filing.options.heading", value)
        }
        onSaveSubheading={(value) =>
          site.save("instant-filing.options.subheading", value)
        }
        body={[
          site.get(
            "instant-filing.options.body.0",
            "xpress-ip is designed for companies, inventors and IP professionals who need reliable European filing support without having to manage the administrative process themselves."
          ),
        ]}
        onSaveBody={(index, value) =>
          site.save(`instant-filing.options.body.${index}`, value)
        }
      />

      <Footer
        editable={site.editMode}
        address1={[
          site.get(
            "instant-filing.footer.address1.0",
            "THE XPRESS IP COMPANY AG"
          ),
          site.get(
            "instant-filing.footer.address1.1",
            "Bundesplatz 9"
          ),
          site.get(
            "instant-filing.footer.address1.2",
            "6302 Zug"
          ),
          site.get(
            "instant-filing.footer.address1.3",
            "Switzerland"
          ),
        ]}
        email={site.get(
          "instant-filing.footer.email",
          "info@xpress-ip.com"
        )}
        tagline={site.get(
          "instant-filing.footer.tagline",
          "EUROPEAN IP FILING, MADE SIMPLE."
        )}
        taglineSub={site.get(
          "instant-filing.footer.taglineSub",
          "Patents · Trademarks · Designs"
        )}
        onSaveAddress1={(index, value) =>
          site.save(`instant-filing.footer.address1.${index}`, value)
        }
        onSaveEmail={(value) =>
          site.save("instant-filing.footer.email", value)
        }
        onSaveTagline={(value) =>
          site.save("instant-filing.footer.tagline", value)
        }
        onSaveTaglineSub={(value) =>
          site.save("instant-filing.footer.taglineSub", value)
        }
      />
    </div>
  )
}
